import { Injectable } from '@angular/core';

@Injectable({
	providedIn: 'root'
})
export class DynamicLibraryLoaderService {
	private readonly loadedLibraries: Set<string> = new Set();

	/**
	 * Dynamically load SweetAlert2 only when needed
	 */
	async loadSweetAlert2(): Promise<any> {
		if (this.loadedLibraries.has('sweetalert2')) {
			return (globalThis as any).Swal;
		}

		const module = await import('sweetalert2');
		this.loadedLibraries.add('sweetalert2');
		return module.default;
	}

	/**
	 * Dynamically load Flowbite only when needed
	 */
	async loadFlowbite(): Promise<void> {
		if (this.loadedLibraries.has('flowbite')) {
			return;
		}

		await import('flowbite');
		this.loadedLibraries.add('flowbite');
	}

	/**
	 * Dynamically load SignalR only when needed
	 */
	async loadSignalR(): Promise<any> {
		if (this.loadedLibraries.has('signalr')) {
			return (globalThis as any).$.connection;
		}

		const script = document.createElement('script');
		script.src = '/node_modules/signalr/jquery.signalR.min.js';
		script.async = true;

		return new Promise((resolve, reject) => {
			script.onload = () => {
				this.loadedLibraries.add('signalr');
				resolve((globalThis as any).$.connection);
			};
			script.onerror = () => {
				const error = new Error(`Failed to load script: ${script.src}`);
				console.error(error.message);
				reject(error);
			};
			document.head.appendChild(script);
		});
	}

	/**
	 * Dynamically load PrintJS only when needed
	 */
	async loadPrintJS(): Promise<any> {
		if (this.loadedLibraries.has('printjs')) {
			return (globalThis as any).printJS;
		}

		const script = document.createElement('script');
		script.src = '/node_modules/print-js/dist/print.js';
		script.async = true;

		return new Promise((resolve, reject) => {
			script.onload = () => {
				this.loadedLibraries.add('printjs');
				resolve((globalThis as any).printJS);
			};
			script.onerror = () => {
				const error = new Error(`Failed to load script: ${script.src}`);
				console.error(error.message);
				reject(error);
			};
			document.head.appendChild(script);
		});
	}

	/**
	 * Dynamically load HTML2Canvas only when needed
	 */
	async loadHtml2Canvas(): Promise<any> {
		if (this.loadedLibraries.has('html2canvas')) {
			return (globalThis as any).html2canvas;
		}

		const module = await import('html2canvas');
		this.loadedLibraries.add('html2canvas');
		return module.default;
	}

	/**
	 * Dynamically load jsPDF only when needed
	 */
	async loadJsPDF(): Promise<any> {
		if (this.loadedLibraries.has('jspdf')) {
			return (globalThis as any).jsPDF;
		}

		const module = await import('jspdf');
		this.loadedLibraries.add('jspdf');
		return module.default;
	}

	/**
	 * Dynamically load jsPDF AutoTable only when needed
	 */
	async loadJsPDFAutoTable(): Promise<any> {
		if (this.loadedLibraries.has('jspdf-autotable')) {
			return (globalThis as any).jsPDF.API.autoTable;
		}

		const module = await import('jspdf-autotable');
		this.loadedLibraries.add('jspdf-autotable');
		return module.default;
	}

	/**
	 * Dynamically load ExcelJS only when needed
	 */
	async loadExcelJS(): Promise<any> {
		if (this.loadedLibraries.has('exceljs')) {
			return (globalThis as any).ExcelJS;
		}

		const module = await import('exceljs');
		this.loadedLibraries.add('exceljs');
		return module.default;
	}

	/**
	 * Dynamically load QRCode only when needed
	 */
	async loadQRCode(): Promise<any> {
		if (this.loadedLibraries.has('qrcode')) {
			return (globalThis as any).QRCode;
		}

		const module = await import('qrcode');
		this.loadedLibraries.add('qrcode');
		return module.default || module;
	}

	/**
	 * Dynamically load jsBarcode only when needed
	 */
	async loadJsBarcode(): Promise<any> {
		if (this.loadedLibraries.has('jsbarcode')) {
			return (globalThis as any).JsBarcode;
		}

		const module = await import('jsbarcode');
		this.loadedLibraries.add('jsbarcode');
		return module.default || module;
	}

	/**
	 * Dynamically load FileSaver only when needed
	 */
	async loadFileSaver(): Promise<any> {
		if (this.loadedLibraries.has('file-saver')) {
			return (globalThis as any).saveAs;
		}

		try {
			const module = await import('file-saver');
			this.loadedLibraries.add('file-saver');
			return module.default;
		} catch (error) {
			console.error('Failed to load FileSaver:', error);
			throw error;
		}
	}

	/**
	 * Dynamically load Lodash only when needed
	 */
	async loadLodash(): Promise<any> {
		if (this.loadedLibraries.has('lodash')) {
			return (globalThis as any)._;
		}

		try {
			const module = await import('lodash');
			this.loadedLibraries.add('lodash');
			return module.default;
		} catch (error) {
			console.error('Failed to load Lodash:', error);
			throw error;
		}
	}

	/**
	 * Dynamically load Moment.js only when needed
	 */
	async loadMoment(): Promise<any> {
		if (this.loadedLibraries.has('moment')) {
			return (globalThis as any).moment;
		}

		try {
			const module = await import('moment');
			this.loadedLibraries.add('moment');
			return module.default;
		} catch (error) {
			console.error('Failed to load Moment:', error);
			throw error;
		}
	}

	/**
	 * Check if a library is already loaded
	 */
	isLibraryLoaded(libraryName: string): boolean {
		return this.loadedLibraries.has(libraryName);
	}

	/**
	 * Get list of loaded libraries
	 */
	getLoadedLibraries(): string[] {
		return Array.from(this.loadedLibraries);
	}
}
